// Global Messages panel — a slide-over reachable from anywhere in the app (the
// header trigger), so users can glance at / continue conversations without leaving
// their current screen. Reuses the same data + components as the full /messages
// page, but keeps its own local active-conversation state (it never changes the
// URL). Mounts its heavy hooks only while open.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { directCounterpart } from './directCounterpart';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/app/hooks';
import { useConversations } from './hooks/useConversations';
import { useDirectory } from './hooks/useDirectory';
import { useTyping } from './hooks/useTyping';
import { useAvailability } from './hooks/useAvailability';
import { InboxList } from './components/InboxList';
import { Thread } from './components/Thread';
import { NewConversationMenu } from './components/NewConversationMenu';
import { useMessagesRoute } from './route';

interface MessagesPanelCtx {
  open: (conversationId?: string) => void;
}

const Ctx = createContext<MessagesPanelCtx>({ open: () => undefined });
export const useMessagesPanel = () => useContext(Ctx);

export const MessagesPanelProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);

  const open = useCallback((id?: string) => {
    setActiveId(id ?? null);
    setIsOpen(true);
  }, []);

  return (
    <Ctx.Provider value={{ open }}>
      {children}
      {isOpen && (
        <PanelBody activeId={activeId} setActiveId={setActiveId} onClose={() => setIsOpen(false)} />
      )}
    </Ctx.Provider>
  );
};

const PanelBody: React.FC<{ activeId: string | null; setActiveId: (id: string | null) => void; onClose: () => void }> = ({
  activeId,
  setActiveId,
  onClose,
}) => {
  const { t } = useTranslation('conversations');
  const navigate = useNavigate();
  const messagesPath = useMessagesRoute();
  const currentUserId = useAppSelector((s) => s.auth.user?.id);
  const roles = useAppSelector((s) => s.auth.user?.roles);
  const directory = useDirectory();
  const { inbox, inboxLoading, messages, messagesLoading, receipts, sendMessage, sendGif, sendPoll, votePoll, toggleReaction, reload, loadOlder, canLoadOlder, loadingOlder } = useConversations(
    activeId,
    currentUserId,
  );
  const [showNew, setShowNew] = useState(false);
  const { notifyTyping, label: typingLabel } = useTyping(activeId, directory.nameOf);
  const panelRef = useRef<HTMLElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  // a11y: Escape closes; move focus into the panel on open and restore it on close.
  useEffect(() => {
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      returnFocusRef.current?.focus?.();
    };
  }, [onClose]);

  const activeSummary = useMemo(() => inbox.find((c) => c.id === activeId), [inbox, activeId]);
  // Same courtesy dot as the full Messages page — one batched read for the DIRECT rows.
  const directPartnerIds = useMemo(
    () =>
      inbox
        .filter((c) => c.type === 'DIRECT')
        .map((c) => directCounterpart(c.participant_user_ids, currentUserId))
        .filter((u): u is string => !!u),
    [inbox, currentUserId],
  );
  const availabilityByUser = useAvailability(directPartnerIds);
  const activeTitle = (() => {
    if (!activeSummary) return 'Conversation';
    if (activeSummary.title) return activeSummary.title;
    if (activeSummary.type === 'DIRECT') {
      const other = directCounterpart(activeSummary.participant_user_ids, currentUserId);
      return directory.nameOf(other) || 'Direct message';
    }
    if (activeSummary.type === 'GROUP') return 'Circle';
    return activeSummary.type.charAt(0) + activeSummary.type.slice(1).toLowerCase();
  })();

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} aria-hidden />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('title')}
        tabIndex={-1}
        className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-card shadow-2xl outline-none"
      >
        <header className="relative flex flex-none items-center gap-2 border-b border-border px-3 py-2.5">
          {activeId ? (
            <button type="button" onClick={() => setActiveId(null)} className="rounded-lg px-1.5 py-1 text-sm text-primary-600 hover:bg-muted">
              ←
            </button>
          ) : null}
          <h2 className="flex-1 truncate text-sm font-semibold text-charcoal">{activeId ? activeTitle : t('title')}</h2>
          {!activeId && (
            <button type="button" onClick={() => setShowNew((v) => !v)} className="rounded-lg bg-primary-500 px-2.5 py-1 text-xs font-semibold text-white hover:bg-primary-600">
              {t('new')}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              onClose();
              navigate(messagesPath(activeId ?? undefined));
            }}
            title={t('open_full')}
            className="rounded-lg px-1.5 py-1 text-muted-foreground hover:bg-muted hover:text-muted-foreground"
          >
            ⤢
          </button>
          <button type="button" onClick={onClose} className="rounded-lg px-1.5 py-1 text-muted-foreground hover:bg-muted hover:text-muted-foreground" aria-label={t('close')}>
            ✕
          </button>
          {showNew && (
            <NewConversationMenu
              directory={directory}
              selfId={currentUserId}
              roles={roles}
              onCreated={(id) => {
                setShowNew(false);
                reload();
                setActiveId(id);
              }}
              onClose={() => setShowNew(false)}
            />
          )}
        </header>

        <div className="min-h-0 flex-1">
          {activeId ? (
            <Thread
              onLoadOlder={loadOlder}
              canLoadOlder={canLoadOlder}
              loadingOlder={loadingOlder}
              receipts={receipts}
              conversationId={activeId ?? undefined}
              title={activeTitle}
              messages={messages}
              loading={messagesLoading}
              currentUserId={currentUserId}
              directory={directory}
              hideHeader
              draftKey={activeId ?? undefined}
              onSend={sendMessage}
              onSendGif={sendGif}
              onSendPoll={sendPoll}
              onVotePoll={votePoll}
              onReact={toggleReaction}
              onTyping={notifyTyping}
              typingLabel={typingLabel}
            />
          ) : (
            <div className="h-full overflow-y-auto">
              <InboxList
                inbox={inbox}
                loading={inboxLoading}
                activeId={activeId}
                selfId={currentUserId}
                nameOf={directory.nameOf}
                avatarOf={(id) => directory.personOf(id)?.avatarUrl}
                availabilityOf={(id) => (id ? availabilityByUser[id] : undefined)}
                onSelect={(id) => setActiveId(id)}
              />
            </div>
          )}
        </div>
      </aside>
    </>
  );
};
